import React, { useEffect, useContext, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity } from 'react-native'
import { Icon, Button, Text, Spinner, useTheme } from '@ui-kitten/components'
import StyledTextInput from '../../components/StyledTextInput'
import { UserContext } from '../../global/UserProvider'
import { saveNewWriteOff, deleteWriteOff } from '../../global/Firebase/firebase'
import ProductList from '../../components/ProductList'
import HeaderIconButton from '../../components/HeaderIconButton'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import * as Sentry from "@sentry/react-native"
import { sendFBAnalyticsEvent } from '../../global/Firebase/firebaseAnalytics'
import { usePostHog } from 'posthog-react-native'
import { useTranslation } from 'react-i18next'
import { productsDictAtom, productCategoriesDictAtom, currencyFormatterAtom, storesDictAtom, userRoleAtom, constantsAtom } from '../../global/AtomsProvider'
import { useAtomValue, atom } from 'jotai'
import { getProductsForWriteOffsByCategoryList } from '../../utils/productListHelpers'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { newFuse } from '../../utils/fuseHelper'
import { calculateTotalPurchasePriceFromIngredients } from '../../utils/ingredientCalculations'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { exceedsStockQuantityLimit, firstUnstorableStockQuantity, MAX_STOCK_QUANTITY } from '../../utils/stockQuantityBounds'
import { saveFailureText } from '../../global/Firebase/writeOutcome'

const productListForWriteOffsAtom = atom(get => getProductsForWriteOffsByCategoryList(get(productsDictAtom), get(productCategoriesDictAtom), false, null))

export default function WriteOffDetailScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productsDict = useAtomValue(productsDictAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const constants = useAtomValue(constantsAtom)
  const postHog = usePostHog()
  const filteredProductListForWriteOff = useAtomValue(productListForWriteOffsAtom)
  const [fuse, setFuse] = useState(newFuse(filteredProductListForWriteOff))
  const { detail, preselectedProduct, preselectedStore, itemIndex, sectionIndex, addItemToData, editItemInData, removeItemFromData } = route.params
  const [store, setStore] = useState(detail ? detail.store : (preselectedStore || context.user.store?.id))
  const [product, setProduct] = useState(detail ? detail.product : (preselectedProduct != null ? preselectedProduct : null))
  // Backend returns reason with proper display text from write_off_reasons table
  const [selectedReason, setSelectedReason] = useState(detail ? detail.reason : null)
  const [quantity, setQuantity] = useState(detail ? detail.quantity.toString() : null)
  const [totalSum, setTotalSum] = useState(detail ? detail.totalSum : 0)
  const [saveError, setSaveError] = useState('')
  const [successMessageVisible, setSuccessMessageVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Debug logging for dual-write e2e tests
  useEffect(() => {
    if (detail) {
      console.log('[WRITEOFF_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        organizationId: detail.organizationId || context.user?.organizationId,
        product: {
          id: detail.product?.id,
          name: detail.product?.name,
          purchasePrice: detail.product?.purchasePrice,
          isComposite: detail.product?.isComposite,
        },
        reason: detail.reason?.id ? detail.reason : { id: detail.reason, text: detail.reason },
        quantity: detail.quantity,
        totalSum: detail.totalSum,
        store: detail.store,
        cashierUID: detail.cashierUID,
      })));
    }
  }, [detail]);

  useEffect(() => {
    if (userRole?.permissions?.deleteWriteOff || context.user.role === 'admin') {
      navigation.setOptions({
        headerRight: () => (
          detail != null && <HeaderIconButton
            onPress={async () => {
              if (!isLoading) {
                try {
                  setIsLoading(true)
                  await deleteWriteOff(db, detail, detail)
                  removeItemFromData?.(sectionIndex, itemIndex)
                  setIsLoading(false)
                  navigation.goBack()
                } catch(error) {
                  console.log(error)
                  setSaveError(t('error'))
                  Sentry.captureException(error, { tags: { books_write_error: 'write-off-delete' } })
                }
              }
            }}
            iconName={isLoading ? 'loader-outline' : 'trash-outline'}
            testID='detail-delete-button'
          />
        ),
      })
    }
  }, [isLoading])

  useEffect(() => {
    // Only calculate totalSum for NEW writeoffs, not when viewing existing ones
    if (detail == null) {
      const updatedTotalSum = (product?.purchasePrice || 0) * quantity
      setTotalSum(updatedTotalSum)
    }
  }, [quantity, detail])

  const chooseProduct = (item) => {
    if (item.isComposite) {
      let newProductInfo = {...item}
      let newIngredients = []
      for (let p of item.ingredients) {
        let newI = {...productsDict[p.id], quantity: p.quantity}
        newIngredients.push(newI)
      }
      newProductInfo.ingredients = newIngredients
      newProductInfo.purchasePrice = calculateTotalPurchasePriceFromIngredients(newIngredients)
      setProduct(newProductInfo)
    } else {
      setProduct(item)
    }
  }

  async function save() {
    if (detail == null) {
      setIsLoading(true)
      const numberValuesToCheck = [quantity]
      let numberAmount
      for (let value of numberValuesToCheck) {
        numberAmount = parseFloat(value)
        if (isNaN(numberAmount)) {
          setSaveError(t('writeOffs.quantity') + ' - ' + t('notANumberError'))
          setIsLoading(false)
          return
        }
        // ATL-960: refuse a quantity the stock ledger cannot hold, BEFORE the Firestore commit
        // below. Firestore has no numeric ceiling and commits first, so without this check an
        // unbounded quantity lands in the customer's books and only the Supabase leg refuses it
        // (numeric(10,4) → 22003) — leaving the two stores disagreeing, with the wrong side being
        // the one the tills read. Refuse and let the user retype; never clamp, because storing a
        // number nobody entered is worse than refusing the write.
        if (exceedsStockQuantityLimit(numberAmount)) {
          setSaveError(t('quantityTooLargeError', { max: MAX_STOCK_QUANTITY }))
          setIsLoading(false)
          return
        }
      }
      // ATL-958: the check above bounds what was TYPED. For a composite assembled at sale time,
      // `saveNewWriteOff` explodes the write-off into its ingredients and sends
      // `quantity * ingredient.quantity` per line — so a composite quantity under the ceiling can
      // still produce an ingredient quantity over it, and `create_writeoff_atomic` raises 22003
      // AFTER the Firestore batch has committed. Group the exploded lines by product exactly as the
      // RPC does (an ingredient listed twice is summed), and refuse before anything is written.
      //
      // An ingredient whose own `quantity` is not a usable number is skipped, not reported here: it
      // is a MALFORMED COMPOSITE, which `saveNewWriteOff` already surfaces on its own (the
      // INGREDIENT_NOT_FOUND branch below, or the Firestore SDK refusing a NaN increment). Calling
      // that "too large" would send the cashier to change a quantity that is not the problem.
      const explodedLines =
        product?.isComposite && !product?.manufacturedBeforeSale && Array.isArray(product?.ingredients)
          ? product.ingredients
              .filter((ingredient) => Number.isFinite(Number(ingredient?.quantity)))
              .map((ingredient) => ({
                ref: ingredient.id,
                name: ingredient.name,
                values: { quantity: numberAmount * Number(ingredient.quantity) },
              }))
          : [{ ref: product?.id, name: product?.name, values: { quantity: numberAmount } }]
      const unstorable = firstUnstorableStockQuantity(explodedLines, ['quantity'])
      if (unstorable) {
        // The ingredient's name, not the composite's: the number the user must change is the typed
        // quantity, but the number that cannot be stored belongs to the ingredient, and a message
        // naming the composite would send them looking at a value that is inside the limit.
        setSaveError(
          unstorable.name
            ? t('writeOffs.ingredientQuantityTooLargeError', {
                name: unstorable.name,
                max: MAX_STOCK_QUANTITY,
              })
            : t('quantityTooLargeError', { max: MAX_STOCK_QUANTITY }),
        )
        setIsLoading(false)
        return
      }
      if (parseFloat(quantity) > 0 && selectedReason != null) {
        setSaveError('')
        const info = {
          product: product,
          reason: selectedReason,
          quantity: parseFloat(quantity),
          totalSum,
          date: new Date(),
          dailyStatID: getDailyStatID(store, new Date()),
          cashierUID: context.user.uid,
          store: store,
          organizationId: context.user.organizationId
        }
        try {
          let ref = await saveNewWriteOff(db, info)
          addItemToData?.({...info, ref, id: ref.id}, info.date, info.store)
          sendFBAnalyticsEvent('new_writeOff')
          postHog.capture('writeoff_created')
          setSuccessMessageVisible(true)
          setIsLoading(false)
        } catch (error) {
          Sentry.captureException(error, { tags: { books_write_error: 'write-off-create' } })
          console.log(error)
          if (error.message === 'INGREDIENT_NOT_FOUND') {
            setSaveError(t('ingredientNotFoundError', { name: error.ingredientName, product: error.productName }))
          } else {
            setSaveError(saveFailureText(error, t, t('error')))
          }
          setIsLoading(false)
        }
      } else {
        setSaveError(t('notAllFieldsFilled'))
        setIsLoading(false)
      }
    }
  }

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
     { product == null ? (
       <ProductList 
        showStoreFilter={true} 
        products={filteredProductListForWriteOff} 
        customFuse={fuse} 
        onItemPress={(item, index, store)=>{
          chooseProduct(item)
          setStore(store)
        }}
        preselectedStore={store}
      />
     ) : (
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        {detail && <Text appearance='hint' style={{textAlign: 'center', marginVertical: 10}}>
          {detail.id}
        </Text>}
        <Text category='label' style={styles.label}>
          {t('product')}
        </Text>
        <Text>{product.name}</Text>
        { Object.values(storesDict).length > 1 && (userRole?.permissions?.['viewOtherStores'] || context.user.role === 'admin') && (
          <>
            <Text category='label' style={styles.label}>
              {t('store')}
            </Text>
            <PlatformDependantSelect
              data={Object.values(storesDict)}
              selectedOption={storesDict?.[store]}
              disabled={detail != null}
              onSelect={(itemValue, itemIndex) => {
                setStore(itemValue.id)
              }}
            />
          </>
        )}
        <Text category='label' style={styles.label}>
          {t('writeOffs.quantity')}
        </Text>
        <StyledTextInput 
          value={quantity}
          onChangeText={text => setQuantity(text.replace(/,/g, '.'))}
          keyboardType='decimal-pad'
          placeholder='0'
          // style={styles.quantityField}
          returnKeyType='done'
          disabled={detail != null}
          selectTextOnFocus={true}
        />
        <View style={[{flexDirection: 'row', alignItems: 'center'}, styles.label]}>
          <Text category='label'>
            {t('writeOffs.reason')}
          </Text>
          { (userRole?.permissions?.['editWriteOffCategories'] || context.user.role === 'admin') && (
            <TouchableOpacity onPress={() => navigation.navigate('EditableList', {atom: constantsAtom, initialDataPath: ['writeOffReasons', 'data'], title: t('writeOffs.writeOffReasons'), ref: constants?.writeOffReasons.ref})} style={{marginLeft: 5, padding: 3}}>
              <Icon name='settings-outline' height={15} width={15} fill={theme['color-primary-500']} />
            </TouchableOpacity>
          )}
        </View>
        <PlatformDependantSelect
          data={constants?.writeOffReasons.data}
          selectedOption={selectedReason}
          disabled={detail != null}
          onSelect={(itemValue, itemIndex) => {
            setSelectedReason(itemValue)
          }}
        />
        <Text category='label' style={styles.label}>
          {t('writeOffs.amount')}
        </Text>
        <Text style={styles.itemTotalPrice} category='s2'>
          {currencyFormatter.format(totalSum)} 
        </Text>
        {saveError != '' && (
          <Text category='p1' status='danger' style={styles.label}>
            {saveError}
          </Text>
        )}
        { successMessageVisible && (
          <>
          <Text status='success'>{t('success')}</Text>
          <Button onPress={() => navigation.goBack()} style={styles.submitButton}>
            {t('back')}
          </Button>
          </>
        )}
        {isLoading ? (
          <View style={styles.spinner}><Spinner /></View>
        ) : (
          !successMessageVisible && (
          <Button onPress={save} style={styles.submitButton} disabled={detail != null}>
            {t('save')}
          </Button>
          )
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
     )}
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  submitButton: {
    marginTop: 16,
  },
  spinner: {
    marginTop: 15,
    alignItems: 'center'
  }
});